// Brujel — Fase 3
//
// Dos pantallas: cargar prendas por foto, y ver el armario con filtros.
// La carga por lote es el camino principal de la app, así que es la que
// se ofrece primero cuando el armario está vacío.

import { useCallback, useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import {
  suscribirFalloDeOpciones,
  opcionesFallaron,
  invalidarOpciones,
} from "./opciones";
import { t, tPlural, useIdioma, fecha as fechaLocal } from "./i18n";
import Login from "./Login";
import Reset from "./Reset";
import Armario from "./Armario";
import CargaLote from "./CargaLote";
import Outfits from "./Outfits";
import Historial from "./Historial";
import Armador from "./Armador";
import Perfumes from "./Perfumes";
import Comprar from "./Comprar";
import Valija from "./Valija";
import Perfil from "./Perfil";
import PrimerUso from "./PrimerUso";
import Ayuda from "./Ayuda";
import * as api from "./api";
/* ⚠️  LA FUENTE VA ANTES QUE LAS HOJAS, Y ES EL ARCHIVO `wght`, NO OTRO.
 *
 * ANTES: define los `@font-face`, y `sistema.css` es quien los nombra
 * en `--tipo-serif`. Al revés funcionaría igual —el orden de los
 * `@font-face` no importa para la cascada— pero leído de arriba abajo
 * este orden cuenta la historia: primero existe la letra, después se
 * la usa.
 *
 * `wght` Y NO `opsz` NI `full`: el paquete trae seis variantes del
 * mismo tipo. `full` son 121 KB porque lleva los cuatro ejes de
 * Fraunces (peso, tamaño óptico, SOFT y WONK); `opsz` son 67 KB.
 * Este es 36,6 KB y trae solo el eje de peso, que es el único que la
 * app usa: no hay ni un `font-variation-settings` en todo el CSS.
 * Pagar 30 u 85 KB por ejes que nadie mueve es peso puro.
 *
 * Los `@font-face` que trae declaran `unicode-range`, así que el
 * archivo no se descarga en los idiomas sin alfabeto latino. Ver
 * `--tipo-serif` en sistema.css. */
import "@fontsource-variable/fraunces/wght.css";
import "./sistema.css";
import "./App.css";
import { useFocoAtrapado } from "./foco";

/* ------------------------------------------------------------------
   Cáscara: barra superior + contenido
------------------------------------------------------------------ */

function AppInterna() {
  // Re-renderiza si cambia el idioma: las pestañas se traducen acá.
  useIdioma();
  const { usuario, perfil, plan, cargando, salir, recargar } = useAuth();

  // Que la bienvenida ya se completó EN ESTA SESIÓN. Existe para que la
  // pantalla desaparezca en el acto al terminar, sin esperar a que /me
  // se vuelva a pedir. La fecha ya quedó en la base, así que la próxima
  // sesión coincide sin este estado.
  const [bienvenidaLista, setBienvenidaLista] = useState(false);

  // El link del mail de recuperación trae el token en el fragmento de
  // la URL. Se lee una sola vez, al montar, y se limpia la barra de
  // direcciones para que el token no quede en el historial.
  //
  // Va en estado y no se recalcula en cada render: si se leyera cada
  // vez, después de limpiar el hash el componente se desmontaría solo
  // a mitad del cambio de contraseña.
  const [recuperacion, setRecuperacion] = useState(() => {
    const r = api.leerTokenDeRecuperacion();
    if (r) api.limpiarHash();
    return r;
  });

  // Link vencido o ya usado: Supabase lo avisa en el mismo fragmento
  if (recuperacion?.error) {
    return (
      <div className="login-fondo">
        <div className="login-caja">
          <p className="login-marca">
            <img src="/logo-brujel.png" alt="Brujel" width="640" height="348" />
          </p>
          <h1 className="login-titulo">{t("app.linkVencido", "Link vencido")}</h1>
          <div className="mensaje-error" role="alert">{recuperacion.error}</div>
          <p className="tenue texto-chico">
            {t("app.linkVencidoExplica", "Los links de recuperación duran poco y sirven una sola vez. Pide uno nuevo desde el login.")}
          </p>
          <button
            className="boton-primario"
            onClick={() => setRecuperacion(null)}
          >
            {t("app.volverLogin", "Volver al login")}
          </button>
        </div>
      </div>
    );
  }

  // Se intercepta ANTES de la protección de rutas: quien viene del mail
  // no tiene sesión iniciada, y sin esto caería en el login sin poder
  // cambiar nada.
  if (recuperacion?.token) {
    return (
      <Reset
        token={recuperacion.token}
        onListo={() => setRecuperacion(null)}
      />
    );
  }

  // Hasta saber si hay sesión no se muestra nada: evita el parpadeo
  // del login antes de entrar.
  if (cargando) {
    return (
      <div className="centrado">
        <p className="tenue">{t("comun.cargando", "Cargando...")}</p>
      </div>
    );
  }

  // Protección de rutas: sin usuario, lo único que existe es el login.
  if (!usuario) return <Login />;

  /* LA BIENVENIDA VA ANTES QUE LA APP, y no como un cartel adentro.
   *
   * Un usuario nuevo entraba a un armario vacío sin nada que lo guiara,
   * y las tres cosas que hacen que la app funcione bien —país,
   * ocasiones, estilo— vivían escondidas en el perfil, que es el último
   * lugar donde alguien mira el primer día.
   *
   * Se decide con `primer_uso_en`, que viene en /me: null significa que
   * todavía no la vio O que la salteó, y las dos veces se marca al
   * terminar. Una bienvenida que vuelve en cada sesión deja de ser una
   * bienvenida.
   *
   * `bienvenidaLista` existe para no depender de que /me se vuelva a
   * pedir: al terminar, la pantalla desaparece en el acto. La fecha ya
   * quedó guardada en la base, así que la próxima sesión coincide.
   */
  /* ⚠️  SE VUELVE A PEDIR /me AL TERMINAR. FALTABA, Y LA PRIMERA
   * PANTALLA DE CADA USUARIO NUEVO MENTÍA (2026-08-24, AL-5).
   *
   * `bienvenidaLista` hace que la pantalla desaparezca en el acto, sin
   * esperar a la red — eso está bien y se conserva. El problema era lo
   * que NO se hacía: el perfil que la app tiene en memoria es el del
   * `/me` de ANTES del onboarding, o sea sin país. Y `estacionActual`
   * se calcula ahí.
   *
   * Entonces alguien elegía "Argentina" en el primer paso, terminaba, y
   * la primera pantalla que veía le decía:
   *
   *     "Sin estación elegida... Cargá tu país en el perfil
   *      para que se elija sola"
   *
   * Ya lo había cargado. Hacía diez segundos. El texto era falso hasta
   * recargar la página a mano, y era la PRIMERA impresión de todos.
   *
   * `recargar()` es `cargarUsuario` de AuthContext: vuelve a pedir /me y
   * recalcula la estación con el país nuevo.
   *
   * NO se espera con await antes de mostrar la app: la pantalla se
   * cierra ya, y el perfil llega un instante después. Que la bienvenida
   * quede colgada por un pedido de red sería cambiar una mentira por
   * una espera.
   */
  if (!perfil?.primer_uso_en && !bienvenidaLista) {
    return (
      <PrimerUso
        onListo={() => {
          setBienvenidaLista(true);
          recargar();
        }}
      />
    );
  }

  return <AppConSesion usuario={usuario} perfil={perfil} plan={plan} salir={salir} />;
}

/* ------------------------------------------------------------------
   La app con sesión iniciada
------------------------------------------------------------------ */

function AppConSesion({ usuario, perfil, plan, salir }) {
  const [pestana, setPestana] = useState("outfits");

  // La prenda ancla: cuando desde el armario pedís "ver con qué combina
  // esto", el id viaja hasta la pantalla de outfits. Vive acá arriba
  // porque cruza pestañas — dentro de cualquiera de las dos se perdería
  // al cambiar.
  const [ancla, setAncla] = useState(null);

  function verCombinaciones(prenda) {
    setAncla(prenda.id);
    setPestana("outfits");
  }

  // Un registro del historial (lista o calendario) que va a CORREGIRSE
  // en "Armar a mano". Desde el 21/9 editar corrige ese registro: ya no
  // crea uno nuevo (ver el modo edición en Armador.js).
  //
  // Vive acá arriba por la misma razón que el ancla: cruza pestañas.
  // Guardado adentro del armador se perdería, porque el armador todavía
  // no está montado cuando se toca "Editar" en el historial.
  //
  // Antes esto no hacía falta: el armador se pedía el historial solo y
  // mostraba "Repetir uno de estos" arriba. Se sacó (ver Armador.js) y
  // el atajo se mudó al calendario, que es donde uno ve qué está
  // eligiendo. La contra es esta: hay que pasarlo de una pestaña a otra.
  const [semilla, setSemilla] = useState(null);

  // Qué inventario muestra Mi armario: prendas o perfumes (22/9). Vive
  // acá para que volver a la pestaña te deje donde estabas.
  const [inventario, setInventario] = useState("prendas");

  function editarOutfit(outfit) {
    setSemilla(outfit);
    setPestana("armar");
  }

  // Cambia cada vez que se guarda una tanda. El armario lo mira para
  // volver a pedir sus datos: si no, guardás 12 prendas, cambiás de
  // pestaña y no están.
  const [recargarToken, setRecargarToken] = useState(0);

  /* ME-8 · las listas caídas se avisan UNA vez, acá.
   *
   * Sin esto, `cargarOpciones()` devuelve todas las listas vacías en
   * silencio y las seis pantallas que las leen pintan selects sin una
   * sola opción. Lo que el usuario ve es un armario al que se le
   * borraron los colores, los tipos y las estaciones — y eso es lo que
   * reporta.
   *
   * Se suscribe acá y no en cada pantalla por la misma razón que
   * `registrarPropios`: una pantalla nueva tendría que acordarse, y no
   * acordarse se ve como un bug de datos.
   */
  const [listasCaidas, setListasCaidas] = useState(opcionesFallaron);
  useEffect(() => suscribirFalloDeOpciones(setListasCaidas), []);

  const [aviso, setAviso] = useState("");

  // EL PERFIL DEJÓ DE SER UNA PESTAÑA (2026-08-20)
  //
  // Eran ocho pestañas y en el celular no entran: la fila se corta o se
  // scrollea, y ninguna de las ocho se lee entera. El perfil es la que
  // menos se abre —se configura una vez y no se vuelve— así que es la
  // que sobra de la fila.
  //
  // Va donde uno ya lo busca: el nombre de arriba a la derecha. Es el
  // lugar donde está en todas las apps que uno usa, y de paso ese
  // nombre pasa de ser un cartel a ser un botón.
  //
  // Se abre como modal y no como pantalla para poder volver a lo que
  // estabas haciendo. Cambiar tu país no debería costarte perder las
  // 24 sugerencias que estabas mirando.
  const [perfilAbierto, setPerfilAbierto] = useState(false);
  // El foco entra al modal al abrir y vuelve al botón al cerrar. Ver
  // src/foco.js: sin esto, con el teclado se tabula por la página de
  // atrás, que está tapada. (BA-14)
  const cajaPerfil = useFocoAtrapado(perfilAbierto);
  /* La Ayuda es un modal desde la cabecera, igual que el perfil y por
   * el mismo motivo: consultarla desde el medio de una tarea no tiene
   * que sacarte de la tarea. Ver la cabecera de src/Ayuda.js. */
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  /* ⚠️  GUARDAR EL PERFIL NO RECARGA LO QUE ESTÁ TAPADO POR EL MODAL.
   *
   * Antes, `alGuardar` subía `recargarToken` en el acto. Como el perfil
   * es un MODAL y no una pestaña, la pantalla de abajo sigue montada:
   * Outfits volvía a pedir `/sugerencias` con los mismos parámetros
   * mientras el modal la tapaba.
   *
   * El refetch en sí está bien —cambiar el perfil cambia el orden— pero
   * el momento estaba mal, y el costo no es un request de más:
   *
   *   UNA IMPRESIÓN SIGNIFICA "ESTAS COMBINACIONES SE LE MOSTRARON AL
   *   USUARIO". Una que se dispara detrás de un modal no se mostró
   *   nunca. Queda sin acción, y la fase 5 la lee como "se le mostraron
   *   y las rechazó todas".
   *
   * O sea que no es ruido: es sesgo sistemático en una sola dirección.
   * Cada usuario que guarda su perfil parado en Outfits genera un
   * rechazo fantasma. Confirmado en producción el 2026-08-24: dos
   * impresiones a 18 segundos, con la MISMA huella de combinaciones.
   *
   * Ahora se recuerda que hubo un cambio y se recarga AL CERRAR, que es
   * cuando la pantalla vuelve a estar a la vista.
   *
   * Va en un `ref` y no en un estado a propósito: el manejador de
   * Escape vive dentro de un efecto que no se vuelve a registrar, así
   * que un estado le quedaría viejo. */
  const perfilTocado = useRef(false);

  const cerrarPerfil = useCallback(() => {
    setPerfilAbierto(false);
    if (perfilTocado.current) {
      perfilTocado.current = false;
      setRecargarToken((n) => n + 1);
    }
  }, []);

  // Con el modal abierto el fondo no se mueve, y Escape cierra. Es el
  // mismo comportamiento que el editor de prendas: dos modales que se
  // portan distinto se sienten como dos apps.
  useEffect(() => {
    if (!perfilAbierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const alTeclear = (e) => {
      if (e.key === "Escape") cerrarPerfil();
    };
    window.addEventListener("keydown", alTeclear);
    return () => {
      document.body.style.overflow = previo;
      window.removeEventListener("keydown", alTeclear);
    };
  }, [perfilAbierto, cerrarPerfil]);

  // Después de registrar un outfit a mano, el armario y el historial
  // quedaron viejos: cambió el último uso de tres prendas.
  /* `irAlHistorial` distingue las DOS formas de registrar un uso, y la
   * diferencia importa:
   *
   *   · Armar a mano  → terminaste una tarea. Llevarte al historial es
   *     mostrarte el resultado.
   *   · Qué me pongo  → estás mirando propuestas y marcaste una.
   *     Sacarte de la pantalla te interrumpe lo que estabas haciendo.
   *
   * El aviso y la recarga van en los dos casos: el armario y el
   * historial tienen que enterarse del uso nuevo venga de donde venga. */
  function alRegistrarOutfit(cuantas, { irAlHistorial = true } = {}) {
    setAviso(t("app.registrado", "Registrado: {prendas}", { prendas: tPlural("plural.prendas", cuantas) }));
    setRecargarToken((n) => n + 1);
    if (irAlHistorial) setPestana("historial");
    setTimeout(() => setAviso(""), 4000);
  }

  /* Corregido o cancelado, se vuelve al historial: es de donde viniste
   * y donde se ve el resultado. La recarga va porque cambió el registro
   * y, con él, la frescura de las prendas que entraron o salieron. */
  function alEditarOutfit() {
    setAviso(t("app.actualizado", "Registro corregido"));
    setRecargarToken((n) => n + 1);
    setPestana("historial");
    setTimeout(() => setAviso(""), 4000);
  }

  /* ⚠️  ACÁ NO VA `setPestana("armario")`. LO TENÍA Y ERA EL PROBLEMA.
   *
   * Guardar una tanda te sacaba de "Cargar" y te dejaba en Mi armario.
   * Volver a Cargar mostraba la pantalla vacía —correctamente: las
   * fichas guardadas salen del borrador porque ya están en la base— así
   * que cargar 200 prendas de a tandas era ir y volver, y cada vuelta
   * empezaba de cero.
   *
   * Es la misma lección que ya está escrita en la cabecera de la valija
   * y en la del borrador de CargaLote: PERDER EL LUGAR ES LO QUE HACE
   * QUE UNO NO SE MOLESTE EN SEGUIR. Con dos usuarios se nota poco; con
   * un armario entero por cargar, decide si la tarea se termina.
   *
   * `recienGuardadas` vive ACÁ y no en CargaLote porque este componente
   * DESMONTA a CargaLote al cambiar de pestaña: si el estado viviera
   * adentro, mirar el armario y volver lo borraría, que es justo lo que
   * se está arreglando.
   *
   * En memoria y NO en localStorage, a propósito: lleva las miniaturas
   * (200-400 KB cada una) y la cuota del navegador son ~5 MB. Es la
   * misma medición por la que el borrador guarda `foto_url` en vez de
   * `vistaPrevia`. Acá no hace falta persistir entre sesiones: la lista
   * existe para encadenar tandas, no para volver mañana. */
  const [recienGuardadas, setRecienGuardadas] = useState([]);

  function alGuardar(cuantas, guardadas) {
    setAviso(t("app.guardaste", "Guardaste {prendas}", { prendas: tPlural("plural.prendas", cuantas) }));
    setRecargarToken((n) => n + 1);
    setRecienGuardadas(guardadas || []);
    setTimeout(() => setAviso(""), 4000);
  }

  /* Cuántos días antes de que termine la prueba se empieza a avisar.
   * Siete: alcanza para reaccionar y no tanto como para que el cartel
   * se vuelva parte del paisaje y deje de leerse. */
  const DIAS_AVISO = 7;

  const avisoDelPlan = (() => {
    if (!plan) return "";
    if (plan.fallaEn) {
      return t("app.avisoCobroFalla", "Tu último cobro falló. Revisa tu medio de pago.");
    }
    if (
      plan.acceso &&
      plan.origen === "prueba" &&
      plan.diasRestantes != null &&
      plan.diasRestantes <= DIAS_AVISO
    ) {
      return t("app.avisoPruebaTermina", "Tu prueba termina el {fecha}.", {
        fecha: fechaLocal(plan.venceEn),
      });
    }
    return "";
  })();

  return (
    <div className="app">
      <header className="barra">
        <h1>
          {/* Las letras del logotipo, recortadas del archivo original —
              no una fuente parecida. El `alt` es lo único que le da
              nombre accesible al <h1> de la app: si se vacía, el
              encabezado principal queda mudo para un lector de
              pantalla. */}
          <img src="/marca-brujel.png" alt="Brujel.com" width="390" height="180" />
        </h1>
        <div className="barra-derecha">
          <button
            className="barra-nombre"
            onClick={() => setPerfilAbierto(true)}
            title={t("app.tuPerfil", "Tu perfil")}
          >
            {perfil?.nombre || usuario.email}
          </button>
          <button className="boton-texto" onClick={() => setAyudaAbierta(true)}>
            {t("app.ayuda", "Ayuda")}
          </button>
          <button className="boton-texto" onClick={salir}>
            {t("app.salir", "Salir")}
          </button>
        </div>
      </header>

      {/* ⚠️  LA FRANJA APARECE SOLO CUANDO URGE, Y ESO SE DECIDIÓ.
          Con el plan vencido NO se pone: el límite ya se lo dice en el
          momento en que intenta agregar una prenda, y un cartel
          permanente sobre alguien que decidió no pagar es acoso, no
          información. Se muestra cuando faltan {DIAS_AVISO} días o
          menos de prueba, y cuando el cobro está fallando — las dos
          situaciones donde todavía se puede hacer algo. */}
      {avisoDelPlan && (
        <div className="franja-plan">
          <span>{avisoDelPlan}</span>
          <button className="boton-texto" onClick={() => setPerfilAbierto(true)}>
            {t("app.verTuPlan", "Ver tu plan")}
          </button>
        </div>
      )}

      {/* Va DESPUÉS de la del plan y antes de las pestañas: si las dos
          aparecen, primero se lee la que habla de la cuenta y después
          la que habla de la conexión. */}
      {listasCaidas && (
        <div className="franja-error" role="alert">
          <span>
            {t(
              "app.listasCaidas",
              "No se pudieron cargar las listas. Lo que ves puede estar incompleto."
            )}
          </span>
          <button
            className="boton-texto"
            onClick={() => {
              /* Se recarga la página entera, y es a propósito. Las seis
                 pantallas ya resolvieron su `cargarOpciones()` y guardaron
                 el resultado en su propio estado; volver a pedir sin que
                 ellas se enteren dejaría la franja apagada y los selects
                 igual de vacíos, que es peor que no tener botón. Y no hay
                 nada que perder: con las listas caídas no se puede llenar
                 ningún formulario. */
              invalidarOpciones();
              window.location.reload();
            }}
          >
            {t("app.reintentar", "Reintentar")}
          </button>
        </div>
      )}

      <nav className="pestanas">
        {/* ⚠️  EL ORDEN LO DECIDIÓ EL DUEÑO (2026-09-19) Y NO ES
            ALFABÉTICO NI CASUAL: va de lo que se hace TODOS LOS DÍAS a
            lo que se hace de vez en cuando.

              qué me pongo · armar a mano   ← vestirse, hoy
              mi armario · historial        ← mirar lo que tengo
              valija · qué comprar          ← planear
              cargar prendas                ← mantenimiento

            "Armar a mano" subió al segundo lugar porque es la otra
            forma de resolver la misma pregunta de la mañana, y estaba
            detrás del armario, que es consulta y no acción.

            ⚠️  SI SE CAMBIA ACÁ, SE CAMBIA EN `Ayuda.js`. El índice de
            la Ayuda espeja este orden a propósito: quien busca ayuda
            la busca donde está el botón. `test_ayuda.js` §1 ata las
            dos listas por contenido; el ORDEN lo ata §1b. */}
        <button
          className={`pestana ${pestana === "outfits" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("outfits")}
        >
          {t("app.tabOutfits", "Qué me pongo")}
        </button>
        <button
          className={`pestana ${pestana === "armar" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("armar")}
        >
          {t("app.tabArmar", "Armar a mano")}
        </button>
        <button
          className={`pestana ${pestana === "armario" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("armario")}
        >
          {t("app.tabArmario", "Mi armario")}
        </button>
        <button
          className={`pestana ${pestana === "historial" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("historial")}
        >
          {t("app.tabHistorial", "Historial")}
        </button>
        <button
          className={`pestana ${pestana === "valija" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("valija")}
        >
          {t("app.tabValija", "Valija")}
        </button>
        <button
          className={`pestana ${pestana === "comprar" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("comprar")}
        >
          {t("app.tabComprar", "Qué comprar")}
        </button>
        <button
          className={`pestana ${pestana === "cargar" ? "pestana-activa" : ""}`}
          onClick={() => setPestana("cargar")}
        >
          {t("app.tabCargar", "Cargar prendas")}
        </button>
      </nav>

      <main className="contenido contenido-ancho">
        {aviso && <div className="mensaje-ok">{aviso}</div>}

        {pestana === "outfits" && (
          <Outfits
            recargarToken={recargarToken}
            ancla={ancla}
            onLimpiarAncla={() => setAncla(null)}
            onRegistrado={(cuantas) =>
              alRegistrarOutfit(cuantas, { irAlHistorial: false })
            }
          />
        )}
        {/* MI ARMARIO TIENE DOS INVENTARIOS (22/9): prendas y perfumes.
            Un conmutador arriba y no una pestaña más: con ocho, la barra
            no entra en el celular (decisión del dueño). */}
        {pestana === "armario" && (
          <>
            <div className="conmutador conmutador-inventario">
              <button
                className={`conmutador-opcion ${inventario === "prendas" ? "conmutador-activa" : ""}`}
                onClick={() => setInventario("prendas")}
                aria-pressed={inventario === "prendas"}
              >
                {t("app.inventarioPrendas", "Prendas")}
              </button>
              <button
                className={`conmutador-opcion ${inventario === "perfumes" ? "conmutador-activa" : ""}`}
                onClick={() => setInventario("perfumes")}
                aria-pressed={inventario === "perfumes"}
              >
                {t("app.inventarioPerfumes", "Perfumes")}
              </button>
            </div>
            {inventario === "perfumes" ? (
              <Perfumes recargarToken={recargarToken} />
            ) : (
              <Armario
                recargarToken={recargarToken}
                onVerCombinaciones={verCombinaciones}
              />
            )}
          </>
        )}
        {pestana === "armar" && (
          <Armador
            alGuardar={alRegistrarOutfit}
            alEditar={alEditarOutfit}
            alCancelarEdicion={() => setPestana("historial")}
            semilla={semilla}
            alUsarSemilla={() => setSemilla(null)}
          />
        )}
        {pestana === "historial" && (
          <Historial
            recargarToken={recargarToken}
            onEditarOutfit={editarOutfit}
          />
        )}
        {pestana === "valija" && <Valija />}
        {pestana === "comprar" && (
          <Comprar
            recargarToken={recargarToken}
            onVerCombinaciones={verCombinaciones}
          />
        )}
        {pestana === "cargar" && (
          <CargaLote alGuardar={alGuardar} recienGuardadas={recienGuardadas} />
        )}
      </main>

      {/* El logotipo completo vive acá: es el único lugar de adentro de
          la app donde hay ancho de sobra y no le roba pantalla a las
          prendas, que son el contenido.

          `aria-hidden`: la marca ya está en el <h1> de la barra. Un
          lector de pantalla que la anuncie dos veces por página no
          informa, estorba. */}
      <footer className="pie">
        <img
          className="pie-logo"
          src="/logo-brujel.png"
          alt=""
          aria-hidden="true"
          width="640"
          height="348"
        />
      </footer>

      {ayudaAbierta && <Ayuda onCerrar={() => setAyudaAbierta(false)} />}

      {perfilAbierto && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-label={t("app.tuPerfil", "Tu perfil")}
          onClick={cerrarPerfil}
        >
          <div className="modal-caja" ref={cajaPerfil} onClick={(e) => e.stopPropagation()}>
            <div className="modal-cabecera">
              <h2 className="modal-titulo">
                {perfil?.nombre || usuario.email}
              </h2>
              <button
                className="modal-cerrar"
                onClick={cerrarPerfil}
                aria-label={t("comun.cerrar", "Cerrar")}
              >
                ×
              </button>
            </div>
            <div className="modal-cuerpo">
              <Perfil
                /* Solo se ANOTA que hubo un cambio. La recarga la hace
                 * `cerrarPerfil`, cuando la pantalla vuelve a verse. */
                alGuardar={() => { perfilTocado.current = true; }}
                onCerrar={cerrarPerfil}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInterna />
    </AuthProvider>
  );
}
